import { GlobalContext } from "components/GlobalContext";
import { IOpportunitiesReportDepartmentTypeResult, IOpportunitiesReportProductResult, IOpportunitiesReportResult } from "models/reports/IOpportunitiesReport";
import moment from "moment";
import { useContext, useRef, useState, useEffect } from "react";
import { Button, Row, Col, Form, Card } from "react-bootstrap";
import ReportService from "services/ReportService";
import { formatCurrency, numberWithCommas } from "utilities/StringUtility";
import './styles/OpportunityReport.scss';

const OpportunitiesReport = () => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    const [startDate, setStartDate] = useState<string>("1/1/2020");
    const [endDate, setEndDate] = useState<string>("1/1/2025");
    const [reportData, setReportData] = useState<IOpportunitiesReportResult>();
    const [forceRefresh, setForceRefresh] = useState<Date>(new Date());
    const [showQuoted, setShowQuoted] = useState<boolean>(false);

    globalContextRef.current = globalContext;
    useEffect(() => {
        if (startDate && endDate) {
            (async () => {
                globalContextRef.current.setLoading(true);
                const accessToken = await globalContextRef.current.getAccessTokenAsync();
                const result = await ReportService.OpportunitiesAsync(accessToken, startDate, endDate);

                if (result) {
                    setReportData(result);
                }

                globalContextRef.current.setLoading(false);
            })();
        }
    }, [forceRefresh]);

    const groupTypes = (types: IOpportunitiesReportDepartmentTypeResult[]) => {
        const result: IOpportunitiesReportDepartmentTypeResult[] = [];
        types.forEach(
            type => {
                if (
                    type.type.toLocaleLowerCase().includes("state government fd") ||
                    type.type.toLocaleLowerCase().includes("federal, dod") ||
                    type.type.toLocaleLowerCase().includes("federal, other")) {
                    const existing = result.find(p => p.type === "State & Federal");
                    if (existing) {
                        existing.count += type.count;
                        existing.total += type.total;
                        existing.quotedTotal += type.quotedTotal;
                    }
                    else {
                        result.push({
                            type: "State & Federal",
                            count: type.count,
                            totalCount: type.totalCount,
                            total: type.total,
                            quotedTotal: type.quotedTotal
                        });
                    }
                }
                else if (
                    type.type.toLocaleLowerCase().includes("all industrial") ||
                    type.type.toLocaleLowerCase().includes("industrial fd") ||
                    type.type.toLocaleLowerCase().includes("industrial, other")) {
                    const existing = result.find(p => p.type === "Industrial");
                    if (existing) {
                        existing.count += type.count;
                        existing.total += type.total;
                        existing.quotedTotal += type.quotedTotal;
                    }
                    else {
                        result.push({
                            type: "Industrial",
                            count: type.count,
                            totalCount: type.totalCount,
                            total: type.total,
                            quotedTotal: type.quotedTotal
                        });
                    }
                }
                else if (
                    type.type.toLocaleLowerCase().includes("other") ||
                    type.type.toLocaleLowerCase().includes("tribal government") ||
                    type.type.toLocaleLowerCase().includes("contract fd") ||
                    type.type.toLocaleLowerCase().includes("transportation fd")) {
                    const existing = result.find(p => p.type === "Other");
                    if (existing) {
                        existing.count += type.count;
                        existing.total += type.total;
                        existing.quotedTotal += type.quotedTotal;
                    }
                    else {
                        result.push({
                            type: "Other",
                            count: type.count,
                            totalCount: type.totalCount,
                            total: type.total,
                            quotedTotal: type.quotedTotal
                        });
                    }
                }
                else {
                    result.push({ ...type });
                }
            });

        return result.sort((a, b) => b.count - a.count);
    };

    const groupProducts = (products: IOpportunitiesReportProductResult[]) => {
        const result: IOpportunitiesReportProductResult[] = [];
        products.forEach(
            product => {
                if (product.name.toLocaleLowerCase().includes("filter")) {
                    const existing = result.find(p => p.name === "Filters");
                    if (existing) {
                        existing.count += product.count;
                        existing.total += product.total;
                    }
                    else {
                        result.push({
                            name: "Filters",
                            count: product.count,
                            total: product.total,
                            imageUrl: product.imageUrl,
                            productId: -1
                        });
                    }
                } else {
                    result.push({ ...product });
                }
            });

        return result.sort((a, b) => b.total - a.total);
    };

    return (
        <>
            <Row>
                <Col>
                    <Card className="mb-3">
                        <Card.Body>
                            <Form>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group>
                                            <Form.Label>Start Date</Form.Label>
                                            {
                                                <Form.Control
                                                    type="date"
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
                                                    value={startDate ? moment(startDate).format('yyyy-MM-DD') : undefined}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group>
                                            <Form.Label>End Date</Form.Label>
                                            {
                                                <Form.Control
                                                    type="date"
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
                                                    value={endDate ? moment(endDate).format('yyyy-MM-DD') : undefined}
                                                />
                                            }
                                        </Form.Group>
                                    </Col >
                                    <Col>
                                        <Form.Group  >
                                            <Form.Label>Show <span className="quoted">Quoted</span> Values</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={async (e) => {
                                                        setShowQuoted(e.target.value == "true");
                                                    }}
                                                    value={showQuoted ? "true" : "false"}>
                                                    {
                                                        ["true", "false"].map((s, index) => <option key={s} value={s}>{s}</option>)
                                                    }
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col >
                                    <Col>
                                        <Button
                                            variant="primary"
                                            className="mt-2"
                                            style={{ width: "100%" }}
                                            onClick={() => setForceRefresh(new Date())}>
                                            Update
                                        </Button>
                                    </Col>
                                </Row >
                            </Form>
                        </Card.Body>
                    </Card >
                </Col>
            </Row>
            <Row className="OpportunityReportResults">
                <Col>
                    {
                        !reportData &&
                        <Row><Col>Please choose a start and end date</Col></Row>
                    }
                    {
                        reportData &&
                        <>
                            <Row className="topSection">
                                <Col>
                                    <Row>
                                        <Col>
                                            <span className="title40" style={{ lineHeight: "1" }}>{numberWithCommas(reportData.count)}</span><br />
                                            <span className="title20">Opportunities</span>
                                        </Col>
                                        <Col>
                                            <span className="title25">{formatCurrency(reportData.totalValue)}</span><br />
                                            {
                                                showQuoted &&
                                                <>
                                                    <span className="title25 quoted">{formatCurrency(reportData.totalQuotedValue)}</span><br />
                                                </>
                                            }
                                            <span style={{ fontSize: "16px" }}>Total Opportunities</span>
                                        </Col>
                                        <Col>
                                            <span className="title25">{formatCurrency(reportData.totalDomesticValue)}</span><br />
                                            {
                                                showQuoted &&
                                                <>
                                                    <span className="title25 quoted">{formatCurrency(reportData.totalQuotedDomesticValue)}</span><br />
                                                </>
                                            }
                                            <span style={{ fontSize: "16px" }}>Domestic Opportunities</span>
                                        </Col>
                                        <Col>
                                            <span className="title25">{formatCurrency(reportData.totalInternationalValue)}</span><br />
                                            {
                                                showQuoted &&
                                                <>
                                                    <span className="title25 quoted">{formatCurrency(reportData.totalQuotedInternationalValue)}</span><br />
                                                </>
                                            }
                                            <span style={{ fontSize: "16px" }}>International Opportunities</span>
                                        </Col>
                                    </Row>
                                    <hr />
                                    <Row>
                                        <Col>
                                            <span style={{ fontSize: "14px" }}>LOW Probability</span><br />
                                            <span className="title30">{formatCurrency(reportData.total10PercentValue + reportData.total20PercentValue + reportData.total30PercentValue)}</span>
                                            {
                                                showQuoted &&
                                                <>
                                                    <br /><span className="title30 quoted">{formatCurrency(reportData.quotedTotal10PercentValue + reportData.quotedTotal20PercentValue + reportData.quotedTotal30PercentValue)}</span>
                                                </>
                                            }
                                        </Col>
                                        <Col>
                                            <span style={{ fontSize: "14px" }}>50/50 Probability</span><br />
                                            <span className="title30">{formatCurrency(reportData.total40PercentValue + reportData.total50PercentValue + reportData.total60PercentValue)}</span>
                                            {
                                                showQuoted &&
                                                <>
                                                    <br /><span className="title30 quoted">{formatCurrency(reportData.quotedTotal40PercentValue + reportData.quotedTotal50PercentValue + reportData.quotedTotal60PercentValue)}</span>
                                                </>
                                            }
                                        </Col>
                                        <Col>
                                            <span style={{ fontSize: "14px" }}>HIGH Probability</span><br />
                                            <span className="title30">{formatCurrency(reportData.total70PercentValue + reportData.total80PercentValue + reportData.total90PercentValue + reportData.total100PercentValue)}</span>
                                            {
                                                showQuoted &&
                                                <>
                                                    <br /><span className="title30 quoted">{formatCurrency(reportData.quotedTotal70PercentValue + reportData.quotedTotal80PercentValue + reportData.quotedTotal90PercentValue + reportData.quotedTotal100PercentValue)}</span>
                                                </>
                                            }
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                            <Row>
                                {
                                    reportData.domesticDealers.length > 0 &&
                                    <Col className="leftSection fullHeightCol" style={{ alignSelf: "flex-start !important" }}>
                                        <span className="title25" style={{ textDecoration: "underline" }}>Domestic</span><br />
                                        <Row>
                                            {
                                                reportData.domesticDealers.map(
                                                    dealer =>
                                                        <Col className="infoBox">
                                                            <Row>
                                                                <Col>
                                                                    <div style={{ minWidth: "100px", width: "100%", height: "100px", backgroundColor: "#fff" }}>
                                                                        {
                                                                            dealer.logoUrl &&
                                                                            <img
                                                                                src={dealer.logoUrl}
                                                                                alt={dealer.dealerName}
                                                                                className="imageContainer" />
                                                                        }
                                                                        {
                                                                            !dealer.logoUrl &&
                                                                            <div className="centerAll" style={{ height: "100%" }}>
                                                                                <span className="noImageTextContainer ">{dealer.dealerName}</span>
                                                                            </div>
                                                                        }
                                                                    </div>
                                                                </Col>
                                                                <Col className="fullHeightCol">
                                                                    <div className="centerAll" style={{ height: "100%" }}>
                                                                        <div >
                                                                            <span className="title20">{numberWithCommas(dealer.count)}</span><br />
                                                                            <span style={{ fontSize: "16px" }}>{formatCurrency(dealer.opportunityTotal)}</span>
                                                                            {
                                                                                showQuoted &&
                                                                                <>
                                                                                    <br /><span style={{ fontSize: "16px" }} className="quoted">{formatCurrency(dealer.opportunityQuotedTotal)}</span>
                                                                                </>
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </Col>
                                                )
                                            }
                                        </Row >

                                        {
                                            reportData.domesticProducts.length > 0 &&
                                            <>
                                                <hr />
                                                <Row>
                                                    {
                                                        groupProducts(reportData.domesticProducts).map(
                                                            product =>
                                                                <Col className="infoBox">
                                                                    <Row>
                                                                        <Col>
                                                                            <div style={{ minWidth: "100px", width: "100%", height: "100px", backgroundColor: "#fff" }}>
                                                                                {
                                                                                    product.imageUrl &&
                                                                                    <img
                                                                                        src={product.imageUrl}
                                                                                        alt={product.name}
                                                                                        className="imageContainer" />
                                                                                }
                                                                                {
                                                                                    !product.imageUrl &&
                                                                                    <div className="centerAll" style={{ height: "100%" }}>
                                                                                        <div
                                                                                            style={{
                                                                                                color: "black",
                                                                                                fontWeight: "bold",
                                                                                                padding: "10px"
                                                                                            }}>{product.name}</div>
                                                                                    </div>
                                                                                }
                                                                            </div>
                                                                        </Col>
                                                                        <Col className="fullHeightCol">
                                                                            <div className="centerAll" style={{ height: "100%" }}>
                                                                                <div >
                                                                                    <span className="title20">{numberWithCommas(product.count)}</span><br />
                                                                                    <span style={{ fontSize: "16px" }}>{formatCurrency(product.total)}</span>
                                                                                </div>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                </Col>
                                                        )
                                                    }
                                                </Row >
                                            </>
                                        }

                                        {
                                            reportData.domesticDepartmentTypes.length > 0 &&
                                            <>
                                                <hr />
                                                <Row>
                                                    {
                                                        groupTypes(reportData.domesticDepartmentTypes).map(
                                                            type =>
                                                                <Col className="infoBox2 centerAll">
                                                                    <div>
                                                                        <span className="title20">{Math.floor((type.count / type.totalCount) * 100)}%</span><br />
                                                                        <span style={{ fontSize: "16px" }}>{(type.type && type.type !== "") ? type.type : "N/A"}</span>
                                                                    </div>
                                                                </Col>
                                                        )
                                                    }
                                                </Row >
                                            </>
                                        }
                                    </Col>
                                }
                                {
                                    reportData.internationalDealers.length > 0 &&
                                    <Col className="rightSection fullHeightCol" style={{ alignSelf: "flex-start !important" }}>
                                        <span className="title25" style={{ textDecoration: "underline" }}>International</span><br />
                                        <Row>
                                            {
                                                reportData.internationalDealers.map(
                                                    dealer =>
                                                        <Col className="infoBox">
                                                            <Row>
                                                                <Col>
                                                                    <div style={{ minWidth: "100px", width: "100%", height: "100px", backgroundColor: "#fff" }}>
                                                                        {
                                                                            dealer.logoUrl &&
                                                                            <img
                                                                                src={dealer.logoUrl}
                                                                                alt={dealer.dealerName}
                                                                                className="imageContainer " />
                                                                        }
                                                                        {
                                                                            !dealer.logoUrl &&
                                                                            <div className="centerAll" style={{ height: "100%" }}>
                                                                                <span className="noImageTextContainer ">{dealer.dealerName}
                                                                                </span>
                                                                            </div>
                                                                        }
                                                                    </div>
                                                                </Col>
                                                                <Col className="fullHeightCol">
                                                                    <div className="centerAll" style={{ height: "100%" }}>
                                                                        <div >
                                                                            <span className="title20">{numberWithCommas(dealer.count)}</span><br />
                                                                            <span style={{ fontSize: "16px" }}>{formatCurrency(dealer.opportunityTotal)}</span>
                                                                            {
                                                                                showQuoted &&
                                                                                <>
                                                                                    <br /><span style={{ fontSize: "16px" }} className="quoted">{formatCurrency(dealer.opportunityQuotedTotal)}</span>
                                                                                </>
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </Col>
                                                )
                                            }
                                        </Row >

                                        {
                                            reportData.internationalProducts.length > 0 &&
                                            <>
                                                <hr />
                                                <Row>
                                                    {
                                                        groupProducts(reportData.internationalProducts).map(
                                                            product =>
                                                                <Col className="infoBox">
                                                                    <Row>
                                                                        <Col>
                                                                            <div style={{ minWidth: "100px", width: "100%", height: "100px", backgroundColor: "#fff", color: "black", fontWeight: "bold" }}>
                                                                                {
                                                                                    product.imageUrl &&
                                                                                    <img
                                                                                        src={product.imageUrl}
                                                                                        alt={product.name}
                                                                                        className="imageContainer" />
                                                                                }
                                                                                {
                                                                                    !product.imageUrl &&
                                                                                    <div className="centerAll" style={{ height: "100%" }}>
                                                                                        <div
                                                                                            style={{
                                                                                                color: "black",
                                                                                                fontWeight: "bold",
                                                                                                padding: "10px"
                                                                                            }}>{product.name}</div>
                                                                                    </div>
                                                                                }
                                                                            </div>
                                                                        </Col>
                                                                        <Col className="fullHeightCol">
                                                                            <div className="centerAll" style={{ height: "100%" }}>
                                                                                <div >
                                                                                    <span className="title20">{numberWithCommas(product.count)}</span><br />
                                                                                    <span style={{ fontSize: "16px" }}>{formatCurrency(product.total)}</span>
                                                                                </div>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                </Col>
                                                        )
                                                    }
                                                </Row >
                                            </>
                                        }

                                        {
                                            reportData.internationalDepartmentTypes.length > 0 &&
                                            <>
                                                <hr />
                                                <Row>
                                                    {
                                                        groupTypes(reportData.internationalDepartmentTypes).map(
                                                            type =>
                                                                <Col className="infoBox2 centerAll">
                                                                    <div>
                                                                        <span className="title20">{Math.floor((type.count / type.totalCount) * 100)}%</span><br />
                                                                        <span style={{ fontSize: "16px" }}>{(type.type && type.type !== "") ? type.type : "N/A"}</span>
                                                                    </div>
                                                                </Col>
                                                        )
                                                    }
                                                </Row >
                                            </>
                                        }

                                    </Col>
                                }

                            </Row>
                        </>
                    }

                </Col>
            </Row>
        </>
    );
}

export default OpportunitiesReport;
